<template>
	<div
		data-cy="range-slider"
		:class="[
			'range-slider',
			{
				'range-slider--is-disabled': disabled,
			}
		]"
	>
		<label class="range-slider__label">
			{{ rangeSliderLabel }}
		</label>

		<div class="range-slider__value">
			<div
				v-if="displayHistogram"
				class="range-slider__chart"
				:style="columns"
			>
				<template v-for="(step, i) in sortedData">
					<div
						v-if="i !== 0"
						:key="i"
						:class="[
							'range-slider__bar',
							{
								'range-slider__bar--inactive': step.value <= slider.min || step.value > slider.max
							}
						]"
						:data-value="step.value"
						:style="{ height: `${Math.floor(step.count * barHeight)}%` }"
					/>
				</template>
			</div>

			<div class="range-slider__track-container">
				<input
					v-model="sliderIndex.min"
					type="range"
					class="range-slider__range-input range-slider__range-input--min"
					:disabled="disabled"
					:min="0"
					:max="steps - 1"
					step="1"
				>

				<input
					v-model="sliderIndex.max"
					type="range"
					class="range-slider__range-input range-slider__range-input--max"
					:disabled="disabled"
					:min="0"
					:max="steps - 1"
					step="1"
				>

				<div class="range-slider__track-wrapper">
					<div
						ref="slider-track"
						class="range-slider__track"
						aria-hidden="true"
					/>
				</div>
			</div>
		</div>

		<div
			v-if="displayFields"
			:class="[
				'range-slider__fields',
				{
					'range-slider__fields--has-label': labelMin || labelMax,
				}
			]"
		>
			<helio-field
				:initial-value="displayMin"
				:append="appendMin"
				:disabled="disabled"
				:prepend="prependMin"
				:icon="iconMin"
				:label="labelMin"
				@blurred="updateValue('min', $event)"
				@enter="updateValue('min', $event)"
			/>

			<div class="range-slider__fields-separator" />

			<helio-field
				:initial-value="displayMax"
				:append="appendMax"
				:disabled="disabled"
				:prepend="prependMax"
				:icon="iconMax"
				:label="labelMax"
				@blurred="updateValue('max', $event)"
				@enter="updateValue('max', $event)"
			/>
		</div>
	</div>
</template>

<script>
import _debounce from 'lodash/debounce';
import Field from '../field/Field.vue';

// Based on https://codepen.io/hugosp/pen/yQwBeO
export default {
	name: 'RangeSlider',

	components: {
		'helio-field': Field,
	},

	props: {
		appendMax: {
			type: String,
			required: false,
			default: null,
			description: 'Sets content that will be added to the right-hand side of the max field',
		},

		appendMin: {
			type: String,
			required: false,
			default: null,
			description: 'Sets content that will be added to the right-hand side of the min field',
		},

		data: {
			type: Array,
			required: true,
			description: 'Sets the values of the different "breakpoints/bars" on the slider',
		},

		disabled: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Allows to disable the range-slider (both sliders and fields)',
		},

		displayFields: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Allows to remove the fields that can allow to directly type the values',
		},

		displayHistogram: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets wether or not the histogram will be displayed',
		},

		formatterToDisplay: {
			type: Function,
			required: false,
			default: null,
			description: 'Allows to pass a custom display formatting function',
		},

		formatterToNumber: {
			type: Function,
			required: false,
			default: null,
			description: 'Allows to pass a custom number formatting function, to cleanup manually typed data',
		},

		histogramHeight: {
			type: Number,
			required: false,
			default: 150,
			description: 'Sets height of histogram',
		},

		iconMax: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the max field icon',
		},

		iconMin: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the min field icon',
		},

		infiniteMax: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets wether or not the histogram has infinite max, which would create a new Infinite value',
		},

		initialMax: {
			type: [Number, String],
			required: false,
			default: null,
			validator: value => typeof value === 'number' || value === 'Infinity',
			description: 'Sets the initial max position of the cursor',
		},

		initialMin: {
			type: Number,
			required: false,
			default: null,
			description: 'Sets the initial min position of the cursor',
		},

		labelMax: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the min label on X axis',
		},

		labelMin: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the max label on X axis',
		},

		prependMax: {
			type: String,
			required: false,
			default: null,
			description: 'Sets content that will be added to the left-hand side of the max field',
		},

		prependMin: {
			type: String,
			required: false,
			default: null,
			description: 'Sets content that will be added to the left-hand side of the min field',
		},

		rangeSliderLabel: {
			type: String,
			required: false,
			default: '',
			description: 'Optional label for the slider',
		},
	},

	data() {
		return {
			axis: {
				min: null,
				max: null,
			},

			sliderIndex: {
				min: null,
				max: null,
			},

			slider: {
				min: null,
				max: null,
			},

			displayMin: null,

			displayMax: null,
		};
	},

	computed: {
		sortedData() {
			const sortedData = [...this.data];

			sortedData.sort((a, b) => a.value - b.value);

			if (this.infiniteMax) {
				sortedData.push({
					count: 0,
					value: 'Infinity',
				});
			}

			return sortedData;
		},

		columns() {
			return {
				'grid-template-columns': `repeat(${this.steps - 1},1fr)`,
				height: `${this.histogramHeight}px`,
			};
		},

		barHeight() {
			return 100 / this.sortedData.reduce((a, b) => (a.count < b.count ? b : a)).count;
		},

		steps() {
			return this.sortedData.length;
		},

		infinityLabels() {
			return ['Infinity', this.$t('range-slider.unlimited')];
		},
	},

	watch: {
		'$i18n.locale'() {
			this.displayMin = this.formatToDisplay(this.slider.min);

			if (this.slider.max === 'Infinity') {
				this.displayMax = this.$t('range-slider.unlimited');
			} else {
				this.displayMax = this.formatToDisplay(this.slider.max);
			}
		},

		'slider.min'(newVal) {
			this.displayMin = this.formatToDisplay(newVal);
		},

		'slider.max'(newVal) {
			if (newVal === 'Infinity') {
				this.displayMax = this.$t('range-slider.unlimited');
			} else {
				this.displayMax = this.formatToDisplay(newVal);
			}
		},

		'sliderIndex.min'(newVal) {
			if (parseFloat(newVal) >= parseFloat(this.sliderIndex.max)) {
				this.sliderIndex.min = this.sliderIndex.max - 1;
			} else if (newVal < 0) {
				this.slider.min = this.sortedData[0].value;
			} else {
				this.slider.min = this.sortedData[newVal].value;
			}

			this.changeTrackWidth();
			this.debouncedEmitChanged(this.slider);
		},

		'sliderIndex.max'(newVal) {
			if (parseFloat(newVal) <= parseFloat(this.sliderIndex.min)) {
				this.sliderIndex.max = this.sliderIndex.min + 1;
			} else if (newVal > this.sortedData.length - 1) {
				this.slider.max = this.sortedData[this.sortedData.length - 1].value;
			} else {
				this.slider.max = this.sortedData[newVal].value;
			}

			this.changeTrackWidth();
			this.debouncedEmitChanged(this.slider);
		},

		sortedData() {
			this.setupData();
		},

		initialMax(newVal) {
			this.setupData();
			this.updateValue('max', newVal);
		},

		initialMin(newVal) {
			this.setupData();
			this.updateValue('min', newVal);
		},
	},

	mounted() {
		this.setupData();
	},

	methods: {
		findMinIndex(num) {
			const lowerItems = this.sortedData.filter(item => num >= item.value);

			return lowerItems.length - 1;
		},

		findMaxIndex(num) {
			const greaterItems = this.sortedData.filter(item => num <= item.value);

			return this.sortedData.length - greaterItems.length;
		},

		formatToDisplay(num) {
			if (this.formatterToDisplay !== null) {
				return this.formatterToDisplay(num);
			}

			return num;
		},

		formatToNumber(num) {
			if (this.formatterToNumber !== null) {
				return this.formatterToNumber(num);
			}

			// Default formatterToNumber
			num = num.replace(' ', '');
			num = num.replace(',', '');
			return parseInt(num, 10);
		},

		setupData() {
			this.slider.min = this.initialMin || this.sortedData[0].value;
			this.slider.max = this.initialMax || this.sortedData[this.sortedData.length - 1].value;

			this.sliderIndex.min = this.initialMin
				? this.findMinIndex(this.initialMin)
				: 0;
			this.sliderIndex.max = this.initialMax
				? this.findMaxIndex(this.initialMax)
				: this.sortedData.length - 1;
		},

		updateValue(type, newVal) {
			if (typeof newVal === 'string' && this.infinityLabels.indexOf(newVal) === -1) {
				newVal = this.formatToNumber(newVal);
			}

			if (type === 'min') {
				if (Number.isNaN(newVal)) {
					this.slider.min = 0;
					this.sliderIndex.min = 0;
				} else {
					const newIndex = this.findMinIndex(newVal);

					if (this.sliderIndex.min === newIndex) {
						this.displayMin = 0;
						this.$nextTick(() => {
							this.displayMin = this.formatToDisplay(this.slider.min);
						});
					} else {
						this.sliderIndex.min = newIndex;
					}
				}
			} else if (type === 'max') {
				if (Number.isNaN(newVal)) {
					this.slider.max = 'Infinity';
					this.sliderIndex.max = this.data.length - 1;
				} else {
					const newIndex = this.findMaxIndex(newVal);

					if (this.sliderIndex.max === newIndex) {
						this.displayMax = 0;
						this.$nextTick(() => {
							this.displayMax = this.infinityLabels.indexOf(newVal) > -1
								? this.$t('range-slider.unlimited')
								: this.formatToDisplay(this.slider.max);
						});
					} else {
						this.sliderIndex.max = newIndex;
					}
				}
			}
		},

		changeTrackWidth() {
			const track = this.$refs['slider-track'];
			const min = (this.sliderIndex.min / (this.steps - 1)) * 100;
			const max = Math.min(100, (this.sliderIndex.max / (this.steps - 1)) * 100);

			if (track !== null) {
				track.style.left = `${min}%`;
				track.style.right = `${100 - max}%`;
			}
		},

		debouncedEmitChanged: _debounce(function debounceEmit(newValue) {
			this.$emit('changed', newValue);
		}, 50),
	},
};
</script>

<style lang="scss" src="./range-slider.scss" />
